import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import type { ImageFile } from "@/App"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  Eraser,
  ArrowCounterClockwise,
  PaintBucket,
  MagicWand,
} from "@phosphor-icons/react"
import { EraserCanvas } from "@/components/EraserCanvas"
import type { EraserCanvasRef } from "@/components/EraserCanvas"

interface EditModalProps {
  image: ImageFile
  isOpen: boolean
  onClose: () => void
  onSave: (url: string) => void
}

type EditMode = "eraser" | "background" | "effects"

const backgroundOptions = [
  { id: "color", label: "Solid Color" },
  { id: "image", label: "Image" },
]

const effectOptions = [
  { id: "none", label: "None" },
  { id: "blur", label: "Blur" },
  { id: "brightness", label: "Bright" },
  { id: "contrast", label: "Contrast" },
]

const predefinedColors = [
  "#ffffff",
  "#000000",
  "#ff0000",
  "#00ff00",
  "#0000ff",
  "#ffff00",
  "#00ffff",
  "#ff00ff",
  "#808080",
  "#c0c0c0",
]

export function EditModal({ image, isOpen, onClose, onSave }: EditModalProps) {
  const [mode, setMode] = useState<EditMode>("eraser")
  const [brushSize, setBrushSize] = useState(20)
  const [erasedImageBlob, setErasedImageBlob] = useState<Blob | null>(null)
  const [canUndo, setCanUndo] = useState(false)
  const eraserRef = useRef<EraserCanvasRef>(null)

  const [bgType, setBgType] = useState("color")
  const [bgColor, setBgColor] = useState("#ffffff")
  const [customBgImage, setCustomBgImage] = useState<File | null>(null)
  const [selectedEffect, setSelectedEffect] = useState("none")
  const [blurValue, setBlurValue] = useState(50)
  const [brightnessValue, setBrightnessValue] = useState(50)
  const [contrastValue, setContrastValue] = useState(50)
  const [exportUrl, setExportUrl] = useState("")
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false)

  const processedURL = useMemo(() => {
    if (!image.processedFile) return ""
    return URL.createObjectURL(image.processedFile)
  }, [image.processedFile])

  useEffect(() => {
    return () => {
      if (processedURL) URL.revokeObjectURL(processedURL)
    }
  }, [processedURL])

  const erasedURL = useMemo(() => {
    if (!erasedImageBlob) return ""
    return URL.createObjectURL(erasedImageBlob)
  }, [erasedImageBlob])

  useEffect(() => {
    return () => {
      if (erasedURL) URL.revokeObjectURL(erasedURL)
    }
  }, [erasedURL])

  // The image source for background/effects is the erased version if available
  const activeImageSource = erasedURL || processedURL

  useEffect(() => {
    if (!isOpen || !image.processedFile) return
    if (mode !== "eraser") {
      applyChanges().catch(() => {})
    }
  }, [
    isOpen,
    mode,
    bgType,
    bgColor,
    customBgImage,
    selectedEffect,
    blurValue,
    brightnessValue,
    contrastValue,
    erasedImageBlob,
  ])

  const getCurrentEffectValue = () => {
    switch (selectedEffect) {
      case "blur":
        return blurValue
      case "brightness":
        return brightnessValue
      case "contrast":
        return contrastValue
      default:
        return 50
    }
  }

  const handleEffectValueChange = (value: number) => {
    switch (selectedEffect) {
      case "blur":
        setBlurValue(value)
        break
      case "brightness":
        setBrightnessValue(value)
        break
      case "contrast":
        setContrastValue(value)
        break
    }
  }

  const applyChanges = useCallback(async () => {
    if (!image.processedFile) return

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const img = new Image()
    img.src = activeImageSource
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error("Failed to load foreground image"))
    })

    canvas.width = img.width
    canvas.height = img.height

    if (bgType === "color") {
      ctx.fillStyle = bgColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    } else if (bgType === "image" && customBgImage) {
      const bgImg = new Image()
      const bgUrl = URL.createObjectURL(customBgImage)
      bgImg.src = bgUrl
      await new Promise<void>((resolve, reject) => {
        bgImg.onload = () => resolve()
        bgImg.onerror = () => reject(new Error("Failed to load background image"))
      })
      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(bgUrl)
    }

    ctx.drawImage(img, 0, 0)

    if (selectedEffect !== "none") {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      switch (selectedEffect) {
        case "blur": {
          const tempCanvas = document.createElement("canvas")
          const tempCtx = tempCanvas.getContext("2d")
          if (!tempCtx) break

          tempCanvas.width = canvas.width
          tempCanvas.height = canvas.height
          tempCtx.drawImage(canvas, 0, 0)
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          ctx.filter = `blur(${blurValue / 10}px)`
          ctx.drawImage(tempCanvas, 0, 0)
          ctx.filter = "none"
          break
        }
        case "brightness":
          for (let i = 0; i < data.length; i += 4) {
            data[i] = Math.min(255, data[i] * (brightnessValue / 50))
            data[i + 1] = Math.min(
              255,
              data[i + 1] * (brightnessValue / 50)
            )
            data[i + 2] = Math.min(
              255,
              data[i + 2] * (brightnessValue / 50)
            )
          }
          ctx.putImageData(imageData, 0, 0)
          break
        case "contrast": {
          const factor =
            (259 * (contrastValue + 255)) / (255 * (259 - contrastValue))
          for (let i = 0; i < data.length; i += 4) {
            data[i] = factor * (data[i] - 128) + 128
            data[i + 1] = factor * (data[i + 1] - 128) + 128
            data[i + 2] = factor * (data[i + 2] - 128) + 128
          }
          ctx.putImageData(imageData, 0, 0)
          break
        }
      }
    }

    const dataUrl = canvas.toDataURL("image/png")
    setExportUrl(dataUrl)
  }, [
    image.processedFile,
    activeImageSource,
    bgType,
    bgColor,
    customBgImage,
    selectedEffect,
    blurValue,
    brightnessValue,
    contrastValue,
  ])

  const handleSave = () => {
    if (mode === "eraser" && erasedURL) {
      onSave(erasedURL)
    } else {
      onSave(exportUrl || activeImageSource)
    }
    onClose()
  }

  const handleEraserUpdate = useCallback((blob: Blob) => {
    setErasedImageBlob(blob)
  }, [])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Image</DialogTitle>
          <DialogDescription>
            Erase areas, change background, or apply effects.
          </DialogDescription>
        </DialogHeader>

        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(val) => val && setMode(val as EditMode)}
          variant="outline"
          className="justify-start"
        >
          <ToggleGroupItem value="eraser" size="sm">
            <Eraser className="mr-1 size-4" />
            Eraser
          </ToggleGroupItem>
          <ToggleGroupItem value="background" size="sm">
            <PaintBucket className="mr-1 size-4" />
            Background
          </ToggleGroupItem>
          <ToggleGroupItem value="effects" size="sm">
            <MagicWand className="mr-1 size-4" />
            Effects
          </ToggleGroupItem>
        </ToggleGroup>

        {mode === "eraser" && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-foreground">
                Brush Size
              </label>
              <Slider
                min={5}
                max={100}
                step={1}
                value={[brushSize]}
                onValueChange={([val]) => setBrushSize(val)}
                className="flex-1"
              />
              <span className="w-8 text-right text-sm text-muted-foreground">
                {brushSize}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => eraserRef.current?.undo()}
                disabled={!canUndo}
              >
                <ArrowCounterClockwise className="mr-1 size-4" />
                Undo
              </Button>
            </div>
            {processedURL && (
              <EraserCanvas
                ref={eraserRef}
                imageSource={processedURL}
                brushSize={brushSize}
                onUpdate={handleEraserUpdate}
                onCanUndoChange={setCanUndo}
              />
            )}
          </div>
        )}

        {mode === "background" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-sm font-medium text-foreground">
                  Background
                </h3>
                <ToggleGroup
                  type="single"
                  value={bgType}
                  onValueChange={(val) => val && setBgType(val)}
                  variant="outline"
                  className="justify-start"
                >
                  {backgroundOptions.map((option) => (
                    <ToggleGroupItem
                      key={option.id}
                      value={option.id}
                      size="sm"
                    >
                      {option.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {bgType === "color" && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-2">
                      {predefinedColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setBgColor(color)}
                          className={`size-8 rounded-full border-2 transition-transform hover:scale-110 ${
                            bgColor === color
                              ? "border-primary ring-2 ring-ring"
                              : "border-border"
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setShowCustomColorPicker(!showCustomColorPicker)
                        }
                      >
                        Custom Color
                      </Button>
                      {showCustomColorPicker && (
                        <input
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="size-8 cursor-pointer rounded border border-input"
                        />
                      )}
                    </div>
                  </div>
                )}

                {bgType === "image" && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setCustomBgImage(e.target.files?.[0] || null)
                    }
                    className="mt-3 w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                  />
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium text-foreground">
                Preview
              </h3>
              <div className="overflow-hidden rounded-lg border">
                <img
                  src={exportUrl || activeImageSource}
                  alt="Preview"
                  className="w-full object-contain"
                />
              </div>
            </div>
          </div>
        )}

        {mode === "effects" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-sm font-medium text-foreground">
                  Effects
                </h3>
                <ToggleGroup
                  type="single"
                  value={selectedEffect}
                  onValueChange={(val) => val && setSelectedEffect(val)}
                  variant="outline"
                  className="justify-start"
                >
                  {effectOptions.map((option) => (
                    <ToggleGroupItem
                      key={option.id}
                      value={option.id}
                      size="sm"
                    >
                      {option.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {selectedEffect !== "none" && (
                  <div className="mt-3">
                    <Slider
                      min={0}
                      max={100}
                      step={1}
                      value={[getCurrentEffectValue()]}
                      onValueChange={([val]) => handleEffectValueChange(val)}
                    />
                    <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                      <span>0</span>
                      <span>{getCurrentEffectValue()}</span>
                      <span>100</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-medium text-foreground">
                Preview
              </h3>
              <div className="overflow-hidden rounded-lg border">
                <img
                  src={exportUrl || activeImageSource}
                  alt="Preview"
                  className="w-full object-contain"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
